import { Component, inject, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { RecaptchaService } from '../../../../core/services/recaptcha.service';
import { EmailService } from '../../../services/email.service';
import { SnackBarService } from '../../../services/snackbar.service';
import { ContactUsRequest } from '../../models/contact-us-request.model';
import { mergeMap, tap } from 'rxjs';
import { SuccessError } from '../success-error/success-error';
import { MatIconModule } from '@angular/material/icon';
import { GtmService } from '../../../../core/services/gtm.service';
import { SeoService } from '../../../services/seo.service';
import { environment } from '../../../../../environments/environment';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '../../../../core/services/auth.service';

@Component({
  selector: 'app-contact-us',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatSelectModule,
    MatOptionModule,
    MatIconModule,
    SuccessError
  ],
  templateUrl: './contact-us.html',
  styleUrl: './contact-us.scss'
})
export class ContactUsComponent implements OnInit {
  private fb = inject(FormBuilder);
  private recaptchaService = inject(RecaptchaService);
  private gtm = inject(GtmService);
  private emailService = inject(EmailService);
  private auth = inject(AuthService);
  private snackbar = inject(SnackBarService);
  private route = inject(ActivatedRoute);

  contactForm: FormGroup;
  loading = false;
  submitted = false;
  contactTopics = ['General', 'Registration', 'Login', 'Sales', 'Other'];
  private seo = inject(SeoService);
  platformId = inject(PLATFORM_ID);

  constructor() {
    this.contactForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      contactMethod: ['email', Validators.required],
      email: [''],
      phone: [''],
      contactTopic: [null, Validators.required],
      message: ['', [Validators.required, Validators.maxLength(1000)]]
    }, { validators: this.atLeastOneContactMethodValidator });

    this.contactForm.get('contactMethod')?.valueChanges.subscribe(method => {
      const emailCtrl = this.contactForm.get('email');
      const phoneCtrl = this.contactForm.get('phone');

      if (method === 'email') {
        emailCtrl?.setValidators([Validators.required, Validators.email]);
        phoneCtrl?.clearValidators();
        phoneCtrl?.setValue('');
      } else {
        phoneCtrl?.setValidators([Validators.required, Validators.pattern(/^\+?[1-9]\d{1,14}$/)]);
        emailCtrl?.clearValidators();
        emailCtrl?.setValue('');
      }

      emailCtrl?.updateValueAndValidity();
      phoneCtrl?.updateValueAndValidity();
    });

    this.contactForm.get('phone')?.valueChanges.subscribe(value => {
      if (this.contactForm.get('contactMethod')?.value !== 'phone') return;
      const formatted = this.formatPhoneNumber(value);
      if (formatted !== value) {
        this.contactForm.get('phone')?.setValue(formatted, { emitEvent: false });
      }
    });

    // Real-time email lowercase + trim
    this.contactForm.get('email')?.valueChanges.subscribe(value => {
      if (this.contactForm.get('contactMethod')?.value !== 'email') return;
      const cleaned = value?.trim().toLowerCase() ?? '';
      if (cleaned !== value) {
        this.contactForm.get('email')?.setValue(cleaned, { emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      document.body.scrollTop = 0;
      const user = this.auth.currentUser();
      if(user != null) {
        this.contactForm.patchValue({
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.username
        });
      } 
      this.gtm.pageView({
        page_path: '/contact',
        page_title: 'Contact Us | Horizinns'
      });
    }

    this.route.params.subscribe(params => {
      const type = params['formType'];
      
      if (type) {
        const matchedTopic = this.contactTopics.find(
          t => t.toLowerCase() === type.toLowerCase()
        );

        if (matchedTopic) {
          this.contactForm.patchValue({ contactTopic: matchedTopic });
        }
      }
      
      const pageTitle = type ? `Contact for ${this.capitalize(type)}` : 'Contact Us';
      this.seo.updateMetaTags(pageTitle, `Contact form for ${type || 'Horizinns'}`, undefined, `${environment.appUrl}/contact/${type || ''}`);
      
      this.gtm.pageView({
        page_path: `/contact/${type || ''}`,
        page_title: pageTitle
      });
    });
  }
  
  atLeastOneContactMethodValidator(group: FormGroup) {
    const method = group.get('contactMethod')?.value;
    const email = group.get('email')?.value?.trim();
    const phone = group.get('phone')?.value?.trim();

    if (method === 'email' && !email) {
      return { contactRequired: true };
    }
    if (method === 'phone' && !phone) {
      return { contactRequired: true };
    }
    return null;
  }

  private formatPhoneNumber(value: string): string {
    if (!value) return '';

    let digits = value.replace(/[^\d+]/g, '');
    if (digits.startsWith('+')) {
      return digits;
    }

    digits = digits.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0,3)}) ${digits.slice(3)}`;
    if (digits.length <= 10) {
      return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
    }
    return digits;
  }

  get f() {
    return this.contactForm.controls as {
      firstName: FormControl<string | null>;
      lastName: FormControl<string | null>;
      contactMethod: FormControl<string | null>;
      email: FormControl<string | null>;
      phone: FormControl<string | null>;
      contactTopic: FormControl<string | null>;
      message: FormControl<string | null>;
    };
  }

  onSubmit() {
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      this.snackbar.open('Please complete all required fields', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.loading = true;

    this.recaptchaService.execute('contact_form_submission').pipe(
      mergeMap(token => {
        const form = this.contactForm.value;
        this.gtm.pushEvent('contact_form_submission', {});
        
        const request: ContactUsRequest = {
          firstName: form.firstName,
          lastName: form.lastName,
          contactTopic: form.contactTopic,
          contactType: form.contactMethod === 'email' ? 'Email' : 'Phone',
          contactData: form.contactMethod === 'email' ? form.email : form.phone,
          message: form.message,
          recaptchaToken: token
        };
        console.log('request', request);
        return this.emailService.sendContactFormSubmission(request);
      }),
      tap({
        next: (result: any) => {
          this.loading = false;
          if (result?.status === 'success') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            this.snackbar.open('Your message has been sent!', 'Close', {
              duration: 6000,
              panelClass: ['success-snackbar'],
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });
            this.submitted = true;
            this.contactForm.reset({ firstName: '', lastName: '', message: '', email: '', phone: '', contactTopic: null, contactMethod: 'email' });
          } else {
            this.showError('There was a problem sending your message.');
          }
        },
        error: () => {
          this.loading = false;
          this.showError('Failed to send. Please try again later.');
        }
      })
    ).subscribe();
  }

  private capitalize(s: string) {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  private showError(msg: string) {
    this.snackbar.open(msg, 'Close', {
      duration: 6000,
      panelClass: ['error-snackbar'],
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }
}